// ─────────────────────────────────────────────────────────────────────────────
// PricingTabComponent
// ─────────────────────────────────────────────────────────────────────────────
// The "Pricing observations" level-1 tab.
// Responsibilities:
//   - Render level-2 sub-tabs (Observations | Imports)
//   - Show a contextual info banner per sub-tab
//   - Host the two lists
//   - Persist the active sub-tab via URL query params (same as products-tab)
//   - Route the "see the observations of this import" link from the journal
//     back to the observations list, filtered on that import
//
// A price source is stored as an ir.attachment, not a knowledge-base document,
// so there is no Documents row for it. The file itself is reached by
// previewing it, from the journal and from every observation it produced.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { ListPriceObservationsComponent } from '../list-price-observations/list-price-observations.component';
import { ListPriceImportsComponent } from '../list-price-imports/list-price-imports.component';

type PricingSubTab = 'observations' | 'imports';

@Component({
  selector: 'app-pricing-tab',
  standalone: true,
  imports: [CommonModule, TranslateModule, SvgIconComponent, ListPriceObservationsComponent, ListPriceImportsComponent],
  templateUrl: './pricing-tab.component.html',
  styleUrls: ['./pricing-tab.component.scss'],
})
export class PricingTabComponent implements OnInit, OnDestroy {
  // ── Tab state ──────────────────────────────────────────────────────────

  activeL2Tab: PricingSubTab = 'observations';

  /**
   * Kept so the journal's "open the observations of this import" link can
   * apply the filter after the sub-tab switch. The list is created by *ngIf,
   * so the reference only exists once the sub-tab is showing — hence the
   * setTimeout in openImportObservations.
   */
  @ViewChild(ListPriceObservationsComponent) observationsList?: ListPriceObservationsComponent;

  // ── Cleanup ────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.activeL2Tab = params['subtab'] === 'imports' ? 'imports' : 'observations';
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Tab navigation ─────────────────────────────────────────────────────

  onL2TabChange(subtab: PricingSubTab): void {
    this.activeL2Tab = subtab;
    this.router.navigate([], { queryParams: { subtab }, queryParamsHandling: 'merge' });
  }

  /** From the import journal: show what that one file produced. */
  openImportObservations(importId: number): void {
    this.onL2TabChange('observations');
    // The list is instantiated by the *ngIf above; wait for that change
    // detection pass before touching it.
    setTimeout(() => this.observationsList?.filterByImport(importId));
  }
}
